import { isNumber } from 'lodash';
import React, { useContext } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type IFontStroke, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { TextPanelContext } from '../../context';
import { useLayerProps } from '../../hooks';
import { Tracker } from '../../utils';
import './style.less';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';

export const StrokeInspector: React.FC = () => {
    const { layerId, recordId } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { textStroke, shapeExtension } = layerData;

        return {
            textStroke: textStroke,
            shapeExtension,
        };
    });

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const strokes = props ? props.textStroke || [] : ([] as IFontStroke[]);
    const shapeExtension = props ? props.shapeExtension : null;

    const max = shapeExtension ? 1 : 3;

    const add = () => {
        if (strokes.length >= max) return;

        editor.getInstance(TextLayerExtension).addStroke(layerId);

        Tracker.addStroke({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'secondary',
        });
    };

    const setVisibility = (index: number, flag: boolean) => {
        editor.getInstance(TextLayerExtension).setEffectVisibility(layerId, 'textStroke', flag, index);

        Tracker.toggleStroke({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'secondary',
        });
    };

    const remove = (index: number) => {
        editor.getInstance(TextLayerExtension).removeEffect(layerId, 'textStroke', index);

        Tracker.removeStroke({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'secondary',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'secondary',

                kind: 'textStroke',
            });
        }
    };

    const adjustColor = (index: number, value: string, options = { writeHistory: true }) => {
        const payload = { idx: index, data: { color: value } };
        editor.getInstance(TextLayerExtension).adjustStrokeProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'secondary',

                prop: 'color',
                value,
            });
        }
    };

    const adjustWidth = (index: number, value: number, options = { writeHistory: true }) => {
        if (!isNumber(value)) return;

        const width = Math.round(value);
        const payload = { idx: index, data: { width } };
        editor.getInstance(TextLayerExtension).adjustStrokeProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'secondary',

                prop: 'width',
                value,
            });
        }
    };

    const adjustOpacity = (index: number, value: number, options = { writeHistory: true }) => {
        if (!isNumber(value)) return;

        const opacity = Math.round(value);
        const hsva = hexToHsva(strokes[index].color);
        const nextColor = hsvaToHexa({ ...hsva, a: opacity / 100 });
        const payload = { idx: index, data: { color: nextColor } };

        editor.getInstance(TextLayerExtension).adjustStrokeProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'secondary',

                prop: 'opacity',
                value,
            });
        }
    };

    return (
        <EffectPropsContainer
            icon="BorderBold"
            title={$t((v) => v.textStyleAdd.stroke)}
            showAdd={strokes.length < max}
            onAdd={add}
        >
            {strokes.length > 0 ? (
                <div className="text-stroke-inspector">
                    {strokes.map((stroke, index) => {
                        const groupName = `${$t((v) => v.textStyleAdd.stroke)}${index + 1}`;
                        const hidden = Boolean(stroke.hidden);

                        const color = stroke.color;
                        const width = stroke.width;
                        const opacity = Math.round(hexToHsva(color).a * 100);

                        return (
                            <div className="text-stroke-inspector__group" key={`stroke-${index}`}>
                                <div className="text-stroke-inspector__toolbar">
                                    <div className="text-stroke-inspector__group-name">{groupName}</div>
                                    <div className="text-stroke-inspector__group-icons">
                                        <div
                                            className="text-stroke-inspector__group-icon"
                                            onClick={() => setVisibility(index, !hidden)}
                                        >
                                            <IconFont name={hidden ? 'InvisibleBold' : 'VisibleBold'} fontSize={14} />
                                        </div>
                                        <div
                                            className="text-stroke-inspector__group-icon"
                                            onClick={() => remove(index)}
                                        >
                                            <IconFont name="TrashCanBold" fontSize={14} />
                                        </div>
                                    </div>
                                </div>

                                <EffectPropsGroup disabled={hidden}>
                                    <div className="text-stroke-inspector__group-content">
                                        {/* 描边颜色 */}
                                        <LabelAction title={$t((v) => v.textStyleAdd.color)} align>
                                            <ColorGroup
                                                popupTriggerSize={{
                                                    width: 58,
                                                    height: 26,
                                                }}
                                                color={color}
                                                setColor={(color) => adjustColor(index, color, { writeHistory: false })}
                                                setColorAfter={(color, _, { isBrandColor }) => {
                                                    adjustColor(index, color, { writeHistory: true });
                                                    if (isBrandColor) {
                                                        window._stat_track?.('hb_layers_func_use', {
                                                            func_id: '1_107',
                                                        });
                                                    }
                                                }}
                                                onOpenChange={handleColorPickerOpenChange}
                                                showIcon={false}
                                                popupOverlayClassName="style-color-picker style-color-row-picker"
                                                placement="left"
                                                onToggleBizTab={(bizTab) => {
                                                    if (bizTab === 'team') {
                                                        window._stat_track?.('hb_layers_func_click', {
                                                            func_id: '1_107',
                                                        });
                                                    }
                                                }}
                                            />
                                        </LabelAction>

                                        {/* 描边范围 */}
                                        <LabelAction title={$t((v) => v.textStyleAdd.range)} align>
                                            <SliderInput
                                                showStepBtn
                                                value={width}
                                                min={0}
                                                max={100}
                                                precision={1}
                                                type="horizontal"
                                                onChange={(value) => adjustWidth(index, value, { writeHistory: false })}
                                                onAfterChange={(value) =>
                                                    adjustWidth(index, value, { writeHistory: true })
                                                }
                                            />
                                        </LabelAction>

                                        {/* 透明度 */}
                                        <LabelAction title={$t((v) => v.textStyleAdd.opacity)} align>
                                            <SliderInput
                                                showStepBtn
                                                value={opacity}
                                                min={0}
                                                max={100}
                                                precision={1}
                                                type="horizontal"
                                                onChange={(value) =>
                                                    adjustOpacity(index, value, { writeHistory: false })
                                                }
                                                onAfterChange={(value) =>
                                                    adjustOpacity(index, value, { writeHistory: true })
                                                }
                                            />
                                        </LabelAction>
                                    </div>
                                </EffectPropsGroup>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
